import { useQuery } from '@tanstack/react-query';
import { getCryptoPriceAtDate } from '../coingecko';

interface PriceAtDateParams {
  coinId: string;
  date: string;
  enabled?: boolean;
}

/**
 * React Query hook for fetching historical cryptocurrency prices
 * Uses CoinGecko API with optimized caching for immutable historical data
 *
 * @param coinId - CoinGecko coin ID (e.g., 'bitcoin', 'ethereum')
 * @param date - Date string in ISO format (e.g., '2024-01-15')
 * @param enabled - Optional flag to enable/disable the query
 * @returns Query result with price in EUR
 */
export const useCryptoPriceAtDate = ({
  coinId,
  date,
  enabled = true
}: PriceAtDateParams) => {
  return useQuery<number | null, Error>({
    queryKey: ['crypto-price-at-date', coinId, date],
    queryFn: () => {
      const dateObj = new Date(date);
      return getCryptoPriceAtDate(coinId, dateObj);
    },
    enabled: enabled && !!coinId && !!date,
    // Prix historiques réussis : Infinity (ils ne changent jamais)
    // Mais React Query ne distingue pas null d'une vraie valeur, donc on utilise
    // une staleTime longue (24h) — l'invalidation explicite gère les corrections de tokens
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 2,
  });
};
